'use client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { CardWithTitle } from '@/components/features/CardWithTitle/CardWithTitle';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { NoResults } from '@/components/features/NoResults/NoResults';
import { PhoneLink } from '@/components/ui/PhoneLink/PhoneLink';
import { Loader } from '@/components/ui/Loader/Loader';
import { useAboutDataContext } from '@/providers/AboutDataProvider';
import { getArticlesList } from '@/api/articles/articles';
import { ArticleItem } from '@/api/articles/articles.types';
import s from './Articles.module.scss';

interface Props {
  currentPage: number;
  query: string;
}

const PER_PAGE = 9;

export const Articles = ({ currentPage, query }: Props) => {
  const { aboutData } = useAboutDataContext();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  const [cards, setCards] = useState<ArticleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);

  const getArticles = async (page: number, search: string) => {
    return await getArticlesList({ page, search, per_page: PER_PAGE });
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      if (searchParams.get('show_more') === 'true') return;

      setIsLoading(true);

      const articles = await getArticles(currentPage, query);

      if (!articles) {
        setIsLoading(false);
        return;
      }

      setCards(articles.items);
      setTotalPages(articles.pages);
      setIsLoading(false);
    };

    fetchDoctors();
  }, [searchParams]);

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;
    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const doctors = await getArticles(nextPage, query);

    setCards((prev) => [...prev, ...doctors.items]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  return (
    <>
      {isLoading && <Loader fixed />}
      <section className={s.articles}>
        <div className="custom-container">
          {cards.length > 0 ? (
            <div className={s.articles__list}>
              {cards.map((card) => (
                <CardWithTitle
                  key={card.id}
                  href={`articles/${card.slug ?? card.id}`}
                  date={card.publication_date}
                  image={card.main_image}
                  title={card.title}
                />))}
            </div>
          ) : (
            <NoResults>
              <p>
                Извините, но запрашиваемые вами статьи не найдены.<br />
                Пожалуйста, измените условия поиска или позвоните нам по телефону <PhoneLink
                phone={aboutData?.main_phone} codeIsFour />.
              </p>
            </NoResults>
          )}

          {cards.length > 0 && currentPage < totalPages && (
            <ShowMoreButton
              onClick={showMore}
              disabled={totalPages < 2}
              className={s.articles__showMore}
            />
          )}
          <Pagination totalPages={totalPages} className={s.articles__pagination} />
        </div>
      </section>
    </>
  );
};